import { useState, useEffect, useMemo } from "react";
import { useWallet } from "@/context/WalletContext";
import { useTranslation } from "@/lib/i18n/useTranslation";
import { Settings2, ScanLine, Copy, Check, ShieldCheck, ChevronUp, ChevronDown, Plus, ArrowUpRight, ArrowDownLeft, Eye, EyeOff } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useApp } from "@/context/AppCtx";
import { SystemProgram, Transaction, Connection, Keypair, PublicKey } from "@solana/web3.js";
import bs58 from "bs58";
import { AlphaTokenRow } from "@/components/AlphaTokenRow";

import { useIsMobile } from "@/hooks/useIsMobile";
import { useWatchdog } from "@/hooks/useWatchdog";
import { useStrategist } from "@/hooks/useStrategist";
import { TokenAvatar } from "@/components/TokenAvatar";
import { fetchNativeSolBalance, fetchAllSplTokensForOwner } from "@/lib/solanaBalance";
import { tokenMetaCache } from "@/lib/tokenMetaCache";

// Phantom-like Wallet Tab
function fmtTimeAgo(timestamp?: number | null) {
  if (!timestamp) return "";
  const mins = Math.max(1, Math.round((Date.now() - timestamp) / 60000));
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

export function WalletTab({ viewMode = "all" }: { viewMode?: "all" | "alpha" | "wallet" } = {}) {
  const { publicKey, secretKey, connected, connect, accounts, activeAccountId, xProfile, connectX, disconnectX, createAccount, switchAccount, updateAccount, importAccount } = useWallet();
  const { t, formatCurrency, locale, setLocale } = useTranslation();
  const isMobile = useIsMobile();
  const { heliusKey, chartAnalytics, setCaMintInput, caMintInput, launchProviderFilter, setLaunchProviderFilter, preferredCurrency, setPreferredCurrency, addAppToast } = useApp() as any;
  const watchdog = useWatchdog();
  const { recommendations } = useStrategist();
  const [solBalance, setSolBalance] = useState<number | null>(null);
  const [splTokens, setSplTokens] = useState<Array<{ mint: string; balance: number; decimals: number }>>([]);
  const [activeSheet, setActiveSheet] = useState<"none" | "receive" | "send" | "settings" | "accounts" | "import">("none");
  const [copied, setCopied] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  const [importKey, setImportKey] = useState("");
  const [importError, setImportError] = useState("");
  const [faqOpen, setFaqOpen] = useState<string | null>(null);
  const [showAllTokens, setShowAllTokens] = useState(false);
  const [alphaSort, setAlphaSort] = useState<"multiplier" | "mcap" | "confidence" | "whale">("multiplier");
  
  // Advanced & Server-side offline features state
  const [isAdvanced, setIsAdvanced] = useState(false);
  const [isSynced, setIsSynced] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [copyTargets, setCopyTargets] = useState<any[]>([]);
  const [targetWallet, setTargetWallet] = useState("");
  const [targetName, setTargetName] = useState("");
  const [buyAmount, setBuyAmount] = useState("0.1");
  
  const activeAccount = useMemo(() => accounts.find(a => a.id === activeAccountId) || accounts[0], [accounts, activeAccountId]);
  
  // Inline action state
  const [activeAction, setActiveAction] = useState<"none" | "receive" | "send">("none");
  const [sendAddress, setSendAddress] = useState("");
  const [sendAmount, setSendAmount] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [sendMsg, setSendMsg] = useState("");
  
  const solPrice = chartAnalytics?.solPrice || 150; // Fallback to 150 if not loaded yet

  // Auto connect if not connected
  useEffect(() => {
    if (!connected) {
      connect();
    }
  }, [connected, connect]);

  const fetchBalance = async () => {
    if (!publicKey) return;
    try {
      const sol = await fetchNativeSolBalance(publicKey);
      if (sol !== null) setSolBalance(sol);
      
      const spl = await fetchAllSplTokensForOwner(publicKey);
      if (spl) setSplTokens(spl);
    } catch (e) {
      console.error("Failed to fetch balance:", e);
    }
  };

  useEffect(() => {
    if (publicKey) {
      fetchBalance();
    }
  }, [publicKey, heliusKey]);

  // Fetch sync status
  useEffect(() => {
    if (!publicKey || !xProfile || activeSheet !== "settings") return;
    
    fetch("/api/auth/me")
      .then(res => res.json())
      .then(data => {
        if (data && data.wallets) {
          const w = data.wallets.find((w: any) => w.publicKey === publicKey);
          setIsSynced(!!w?.encryptedSecretKey);
        }
      })
      .catch(() => {});
  }, [publicKey, activeSheet, xProfile]);

  // Fetch copy trade config
  const fetchCopyTargets = async () => {
    try {
      const res = await fetch("/api/copy-trade");
      const data = await res.json();
      if (Array.isArray(data)) {
        // filter for active wallet
        setCopyTargets(data.filter((cfg: any) => cfg.followerWallet === publicKey));
      }
    } catch {}
  };

  useEffect(() => {
    if (xProfile && activeSheet === "settings" && publicKey) {
      fetchCopyTargets();
    }
  }, [xProfile, activeSheet, publicKey]);

  const handleSyncWallet = async () => {
    if (!secretKey || !publicKey || isSyncing) return;
    setIsSyncing(true);
    try {
      const dbWalletId = activeAccount?.id || `wallet-${Date.now()}`;
      const res = await fetch(`/api/wallets/${dbWalletId}/sync`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ secretKey })
      });
      if (res.ok) {
        setIsSynced(true);
      } else {
        const err = await res.json();
        addAppToast(err.error || "Failed to sync wallet offline", "error");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleUnsyncWallet = async () => {
    if (isSyncing) return;
    setIsSyncing(true);
    try {
      const dbWalletId = activeAccount?.id || "";
      const res = await fetch(`/api/wallets/${dbWalletId}/unsync`, {
        method: "POST"
      });
      if (res.ok) {
        setIsSynced(false);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleAddWhale = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!targetWallet.trim() || !publicKey) return;
    try {
      const res = await fetch("/api/copy-trade", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          followerWallet: publicKey,
          targetWallet: targetWallet.trim(),
          name: targetName.trim() || "Whale Wallet",
          buyAmountSol: parseFloat(buyAmount) || 0.1,
          maxDailySol: 5
        })
      });
      if (res.ok) {
        setTargetWallet("");
        setTargetName("");
        fetchCopyTargets();
      } else {
        const err = await res.json();
        addAppToast(err.error || "Failed to register whale", "error");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteWhale = async (id: string) => {
    try {
      const res = await fetch(`/api/copy-trade/${id}`, { method: "DELETE" });
      if (res.ok) {
        fetchCopyTargets();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const copyAddress = async () => {
    if (!publicKey) return;
    await navigator.clipboard.writeText(publicKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const copySecret = async () => {
    if (!secretKey) return;
    await navigator.clipboard.writeText(secretKey || "");
  };

  const handleConnectX = () => {
    connectX();
  };

  const handleCreateAccount = () => {
    createAccount();
    setActiveSheet("none");
  };

  const handleSend = async () => {
    if (!secretKey || !heliusKey || !sendAddress || !sendAmount || isNaN(Number(sendAmount))) return;
    setIsSending(true);
    setSendMsg("");
    try {
      const connection = new Connection(`https://mainnet.helius-rpc.com/?api-key=${heliusKey}`);
      const userKeypair = Keypair.fromSecretKey(bs58.decode(secretKey));
      const toPubkey = new PublicKey(sendAddress);
      
      const lamports = Math.floor(Number(sendAmount) * 1e9);
      const tx = new Transaction().add(
        SystemProgram.transfer({
          fromPubkey: userKeypair.publicKey,
          toPubkey,
          lamports
        })
      );
      
      const { blockhash } = await connection.getLatestBlockhash();
      tx.recentBlockhash = blockhash;
      tx.feePayer = userKeypair.publicKey;
      
      tx.sign(userKeypair);
      const sig = await connection.sendRawTransaction(tx.serialize());
      setSendMsg(`Success! Sig: ${sig.slice(0, 8)}...`);
      setSendAmount("");
      setSendAddress("");
      setTimeout(() => fetchBalance(), 3000);
    } catch (e) {
      setSendMsg(`Error: ${e instanceof Error ? e.message : "Failed to send"}`);
    } finally {
      setIsSending(false);
    }
  };

  const handlePhantomFund = async () => {
    try {
      const provider = (window as any).solana;
      if (!provider || !provider.isPhantom) {
        window.open("https://phantom.app/", "_blank");
        return;
      }
      const resp = await provider.connect();
      const phantomPubkey = new PublicKey(resp.publicKey.toString());
      
      const amount = prompt("How much SOL would you like to deposit?", "0.1");
      if (!amount || isNaN(Number(amount))) return;
      
      const connection = new Connection(`https://mainnet.helius-rpc.com/?api-key=${heliusKey}`);
      const transaction = new Transaction().add(
        SystemProgram.transfer({
          fromPubkey: phantomPubkey,
          toPubkey: new PublicKey(publicKey!),
          lamports: Math.floor(Number(amount) * 1e9)
        })
      );
      
      const { blockhash } = await connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = phantomPubkey;
      
      const signed = await provider.signTransaction(transaction);
      const signature = await connection.sendRawTransaction(signed.serialize());
      addAppToast(`Deposit successful! Tx: ${signature.slice(0, 8)}...`, "success");
      setTimeout(fetchBalance, 3000);
    } catch (err) {
      console.error(err);
      addAppToast("Failed to deposit from Phantom.", "error");
    }
  };

  const shortAddress = useMemo(() => {
    if (!publicKey) return "";
    return `${publicKey.slice(0, 4)}...${publicKey.slice(-4)}`;
  }, [publicKey]);

  // Real Tokens List for UI with real icons and animated avatars (No dummy data)
  const tokens = useMemo(() => {
    const list: Array<{
      mint: string;
      symbol: string;
      name: string;
      balance: number;
      price: number;
      imageUri?: string | null;
      badge?: string;
    }> = [
      {
        mint: "So11111111111111111111111111111111111111112",
        symbol: "SOL",
        name: "Solana",
        balance: solBalance || 0,
        price: solPrice || 178.5,
        imageUri: "https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/So11111111111111111111111111111111111111112/logo.png",
        badge: "SOL",
      }
    ];

    for (const t of splTokens) {
      const cached = tokenMetaCache.get(t.mint);
      const rev = watchdog?.revivals?.find(r => r.mint === t.mint);
      const multi = watchdog?.multipliers?.find(m => m.mint === t.mint);
      const rec = recommendations.find(r => r.mint === t.mint);

      const symbol = cached?.symbol || rev?.symbol || multi?.symbol || rec?.coin || `${t.mint.slice(0, 4)}...${t.mint.slice(-4)}`;
      const name = cached?.name || rev?.name || multi?.name || rec?.name || `Token ${t.mint.slice(0, 4)}`;
      const imageUri = cached?.imageUri || rev?.image || multi?.image || rec?.imageUri || null;

      // Determine approximate price
      let price = 0;
      if (rev?.mcUsd) price = rev.mcUsd / 1e9;
      else if (multi?.mcUsd) price = multi.mcUsd / 1e9;
      else if (rec?.marketCapSol) price = (rec.marketCapSol * (solPrice || 178.5)) / 1e9;

      list.push({
        mint: t.mint,
        symbol,
        name,
        balance: t.balance,
        price,
        imageUri,
        badge: "🪙",
      });
    }

    return list;
  }, [solBalance, solPrice, splTokens, watchdog?.revivals, watchdog?.multipliers, recommendations]);

  const totalUsdValue = useMemo(() => {
    return tokens.reduce((acc, t) => acc + t.balance * t.price, 0);
  }, [tokens]);

  return (
    <div className={`mobile-scroll-child relative overflow-hidden w-full h-full flex flex-col pointer-events-auto pb-24 custom-scrollbar ${isMobile ? "bg-transparent pt-14" : "bg-[#121212]"}`}>
      {/* Top Bar */}
      {viewMode !== "alpha" && (
        <>
          <div className={`sticky top-0 z-20 flex items-center justify-between px-5 pt-6 pb-2 ${isMobile ? "bg-black/40 backdrop-blur-xl" : "bg-[#121212]"}`}>
            <div className="flex items-center gap-3 cursor-pointer hover:opacity-80" onClick={() => setActiveSheet("accounts")}>
              <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-purple-500 to-indigo-500 flex items-center justify-center border border-white/10 shadow-lg shadow-purple-500/20">
                <span className="text-white font-bold text-sm">{activeAccount?.name?.charAt(0) || "W"}</span>
              </div>
              <div className="flex flex-col">
                <div className="flex items-center gap-1 text-white/50 text-[10px] font-bold uppercase tracking-wider">
                  {activeAccount?.name || "Account 1"} <ChevronDown className="size-3" />
                </div>
                <button 
                  onClick={(e) => { e.stopPropagation(); copyAddress(); }}
                  className="flex items-center gap-1.5 text-white hover:text-purple-400 transition-colors"
                >
                  <span className="font-mono text-xs">{shortAddress}</span>
                  {copied ? <Check className="size-3 text-emerald-400" /> : <Copy className="size-3 text-white/40" />}
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => {}}
                className="w-9 h-9 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/80 transition-colors"
                title="Scan QR (Not implemented in inline mode)"
              >
                <ScanLine className="size-4" />
              </button>
              <button 
                onClick={() => setActiveSheet("settings")}
                className="w-9 h-9 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/80 transition-colors"
              >
                <Settings2 className="size-4" />
              </button>
            </div>
          </div>

          {!publicKey && (
            <div className="px-5 pb-4">
              <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-slate-900/90 to-slate-800/70 p-4 shadow-[0_0_0_1px_rgba(255,255,255,0.02)]">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-cyan-400/80">{t("wallet.xAuthTitle")}</p>
                    <p className="mt-1 text-sm text-white/70">{xProfile ? t("wallet.xConnectedLabel", { handle: xProfile.username }) : t("wallet.xPrompt")}</p>
                  </div>
                  {xProfile ? (
                    <button onClick={disconnectX} className="rounded-full border border-white/10 px-3 py-1 text-[11px] font-semibold text-white/70 transition hover:bg-white/10">
                      {t("wallet.disconnectX")}
                    </button>
                  ) : null}
                </div>
                {!xProfile ? (
                  <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                    <select
                      value={locale}
                      onChange={(e) => setLocale(e.target.value as any)}
                      className="w-full sm:w-24 rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-sm text-white outline-none ring-0 appearance-none"
                    >
                      <option value="en">English</option>
                      <option value="es">Español</option>
                      <option value="zh">中文</option>
                      <option value="ja">日本語</option>
                      <option value="ru">Русский</option>
                      <option value="de">Deutsch</option>
                      <option value="fr">Français</option>
                      <option value="pt">Português</option>
                    </select>
                    <select
                      value={preferredCurrency}
                      onChange={(e) => setPreferredCurrency(e.target.value)}
                      className="w-full sm:w-24 rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-sm text-white outline-none ring-0 appearance-none"
                    >
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                      <option value="JPY">JPY</option>
                      <option value="CNY">CNY</option>
                    </select>
                    <button onClick={handleConnectX} className="flex-1 rounded-xl bg-cyan-500/90 px-4 py-2 text-sm font-semibold text-slate-950 transition hover:bg-cyan-400">
                      {t("wallet.connectX")}
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          )}

          {/* Balance & Funding Section */}
          <div className="flex flex-col items-center justify-center pt-2 pb-6 px-5">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-4xl font-black tracking-tight text-white">
                {formatCurrency ? formatCurrency(totalUsdValue) : `$${totalUsdValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              </span>
            </div>
            <div className="flex items-center gap-2 text-xs font-mono text-emerald-400 bg-emerald-500/10 px-2.5 py-1 rounded-full border border-emerald-500/20 mb-6">
              <span>+{solBalance ? (solBalance * (solPrice || 178)).toFixed(2) : "0.00"} SOL</span>
              <span className="text-white/40">•</span>
              <span>100% On-chain</span>
            </div>

            {/* Direct Funding / Onboarding UI */}
            <div className="w-full max-w-sm bg-black/40 border border-white/10 rounded-3xl p-5 flex flex-col items-center shadow-[0_0_40px_rgba(0,0,0,0.5)]">
              <div className="grid grid-cols-2 gap-2 w-full mb-4">
                <button 
                  onClick={() => setActiveAction(activeAction === "receive" ? "none" : "receive")}
                  className={`flex items-center justify-center gap-2 py-2.5 rounded-xl border transition-all text-[11px] font-bold text-white group ${activeAction === "receive" ? "bg-white/10 border-white/20" : "bg-white/5 hover:bg-white/10 border-white/10"}`}
                >
                  <ArrowDownLeft className="size-3.5 text-purple-400 group-hover:scale-110 transition-transform" />
                  {t("wallet.receive")}
                </button>
                <button 
                  onClick={() => setActiveAction(activeAction === "send" ? "none" : "send")}
                  className={`flex items-center justify-center gap-2 py-2.5 rounded-xl border transition-all text-[11px] font-bold text-white group ${activeAction === "send" ? "bg-white/10 border-white/20" : "bg-white/5 hover:bg-white/10 border-white/10"}`}
                >
                  <ArrowUpRight className="size-3.5 text-white/60 group-hover:scale-110 transition-transform" />
                  {t("wallet.send")}
                </button>
              </div>

              {activeAction === "none" && (
                <div className="w-full">
                  <button 
                    onClick={handlePhantomFund}
                    className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-gradient-to-r from-purple-500/20 to-indigo-500/20 border border-purple-500/30 hover:border-purple-500/60 transition-all text-[11px] font-bold text-white group"
                  >
                    <ArrowDownLeft className="size-3.5 text-purple-400 group-hover:scale-110 transition-transform" />
                    {t("wallet.deposit")} (Phantom)
                  </button>
                </div>
              )}

              {activeAction === "receive" && (
                <div className="flex flex-col items-center w-full animate-in fade-in zoom-in-95 duration-200">
                  <div className="p-3 bg-white rounded-2xl shadow-xl shadow-white/5 mb-4">
                    {publicKey ? (
                      <QRCodeSVG
                        value={publicKey}
                        size={140}
                        bgColor={"#ffffff"}
                        fgColor={"#000000"}
                        level={"L"}
                        includeMargin={false}
                      />
                    ) : (
                      <div className="w-[140px] h-[140px] bg-gray-200 animate-pulse rounded-xl" />
                    )}
                  </div>
                  <button 
                    onClick={(e) => { e.stopPropagation(); copyAddress(); }}
                    className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors border border-white/5 font-mono text-xs text-white"
                  >
                    {shortAddress}
                    {copied ? <Check className="size-4 text-emerald-400" /> : <Copy className="size-4 text-white/40" />}
                  </button>
                </div>
              )}

              {activeAction === "send" && (
                <div className="flex flex-col gap-3 w-full animate-in fade-in zoom-in-95 duration-200 text-left">
                  <div className="space-y-1">
                    <label className="text-white/60 text-[10px] font-bold uppercase tracking-wider">{t("wallet.recipient")}</label>
                    <div className="flex gap-2">
                      <input 
                        type="text" 
                        value={sendAddress}
                        onChange={(e) => setSendAddress(e.target.value)}
                        placeholder="Solana address"
                        className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-xs text-white font-mono placeholder:text-white/20 focus:outline-none focus:border-purple-500/50"
                      />
                      <button onClick={() => {}} className="px-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-white/60 hover:text-white transition-colors">
                        <ScanLine className="size-4" />
                      </button>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-white/60 text-[10px] font-bold uppercase tracking-wider">{t("wallet.amount")} (SOL)</label>
                    <input 
                      type="number" 
                      value={sendAmount}
                      onChange={(e) => setSendAmount(e.target.value)}
                      placeholder="0.0"
                      min="0"
                      step="0.01"
                      className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-xs text-white font-mono placeholder:text-white/20 focus:outline-none focus:border-purple-500/50"
                    />
                  </div>
                  
                  {sendMsg && (
                    <div className={`p-2 rounded-lg text-xs font-mono break-all ${sendMsg.includes("Success") ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" : "bg-rose-500/10 text-rose-400 border border-rose-500/20"}`}>
                      {sendMsg}
                    </div>
                  )}

                  <button 
                    onClick={handleSend}
                    disabled={isSending || !sendAddress || !sendAmount}
                    className="w-full mt-2 bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-400 hover:to-indigo-400 disabled:opacity-50 disabled:grayscale text-white font-bold py-2.5 rounded-xl transition-all text-xs"
                  >
                    {isSending ? "..." : t("wallet.confirmSend")}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Token Holdings List */}
          <div className="px-5 pb-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold uppercase tracking-wider text-white/50">Token Holdings</span>
              <span className="text-xs font-mono text-white/40">{tokens.length} Assets</span>
            </div>

            <div className="space-y-2">
              {(showAllTokens ? tokens : tokens.slice(0, 3)).map((token) => (
                <div 
                  key={token.mint}
                  onClick={() => setCaMintInput(token.mint)}
                  className="flex items-center justify-between p-3 rounded-2xl bg-white/5 hover:bg-white/10 cursor-pointer transition-all border border-white/[0.02]"
                >
                  <div className="flex items-center gap-3">
                    <TokenAvatar mint={token.mint} symbol={token.symbol} name={token.name} imageUri={token.imageUri} badge={token.badge} size="size-12 md:size-14" />
                    <div className="flex flex-col">
                      <span className="text-white font-bold text-sm">{token.name || token.symbol}</span>
                      <span className="text-white/50 text-xs font-mono">{token.balance.toLocaleString(undefined, { maximumFractionDigits: 4 })} {token.symbol}</span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end">
                    <span className="text-white font-bold text-sm font-mono">${(token.balance * (token.price ?? 0)).toFixed(2)}</span>
                    <span className="text-white/60 text-xs font-mono">{token.balance.toLocaleString(undefined, { maximumFractionDigits: 2 })} {token.symbol}</span>
                  </div>
                </div>
              ))}
            </div>

            {tokens.length > 3 && (
              <button
                onClick={() => setShowAllTokens(v => !v)}
                className="w-full mt-2 py-2 text-[11px] font-black uppercase tracking-widest text-white/40 hover:text-white/70 transition-colors"
              >
                {showAllTokens ? `Show Less ↑` : `See All ${tokens.length} Tokens ↓`}
              </button>
            )}
          </div>
        </>
      )}

      {/* Unified Alpha Picks — revivals + multipliers + targets with sort tabs */}
      {viewMode !== "wallet" && (watchdog.revivals.length > 0 || (watchdog.multipliers && watchdog.multipliers.length > 0) || recommendations.filter(r => !r.isDead && !r.isRugged).length > 0) && (
        <div className="bg-[#1a1b1e] border-t border-white/5 p-5">
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-bold flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              Alpha Picks
            </h3>
            <span className="text-xs font-bold text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded">Live</span>
          </div>

          {/* Sort tabs */}
          <div className="flex gap-1 mb-4 bg-black/30 rounded-xl p-1">
            {([
              { id: "multiplier", label: "🔥 Mult" },
              { id: "mcap", label: "📈 MCAP" },
              { id: "confidence", label: "🧠 AI" },
              { id: "whale", label: "🐋 Whale" },
            ] as const).map(({ id, label }) => (
              <button
                key={id}
                onClick={() => setAlphaSort(id)}
                className={`flex-1 py-1.5 text-[10px] font-black uppercase tracking-wide rounded-lg transition-all ${
                  alphaSort === id
                    ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
                    : "text-white/40 hover:text-white/70"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

                    {/* Multiplier sort — from watchdog.multipliers */}
          {alphaSort === "multiplier" && (
            <div className="space-y-4">
              {[...( watchdog.multipliers || [])]
                .sort((a, b) => (b.maxMultiplier ?? 0) - (a.maxMultiplier ?? 0))
                .map((r, i) => (
                  <AlphaTokenRow
                    key={i}
                    target={{
                      mint: r.mint,
                      coin: r.symbol || r.mint.slice(0, 6),
                      name: r.name,
                      imageUri: r.image,
                      badge: "🔥",
                      mcUsd: r.mcUsd,
                      multiplier: r.maxMultiplier,
                      top1: r.top1,
                      creatorPct: r.creatorPct,
                      baselineMcUsd: r.baselineMcUsd,
                      at: r.at
                    }}
                    isSelected={caMintInput === r.mint}
                    onOpen={setCaMintInput}
                  />
                ))}
              {(!watchdog.multipliers || watchdog.multipliers.length === 0) && (
                <p className="text-center text-white/30 text-xs py-6">No multipliers tracked yet</p>
              )}
            </div>
          )}

          {/* MCAP sort — from revivals, sorted by mcUsd */}
          {alphaSort === "mcap" && (
            <div className="space-y-2">
              {[...watchdog.revivals]
                .sort((a, b) => (b.mcUsd ?? 0) - (a.mcUsd ?? 0))
                .map((r, i) => {
                  const growth = r.baselineMcUsd && r.mcUsd ? ((r.mcUsd - r.baselineMcUsd) / r.baselineMcUsd) * 100 : 0;
                  return (
                    <div
                      key={i}
                      onClick={() => setCaMintInput(r.mint)}
                      className="flex items-center justify-between p-3 rounded-2xl bg-white/5 hover:bg-emerald-500/10 cursor-pointer transition-all border border-white/[0.02] hover:border-emerald-500/40 group"
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <TokenAvatar mint={r.mint} symbol={r.symbol} name={r.name} imageUri={r.image} badge="🚀" size="size-12 md:size-14" />
                        <div className="flex flex-col min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-white font-bold text-sm group-hover:text-emerald-300 transition-colors truncate">
                              {r.symbol || r.mint.slice(0, 8)}
                            </span>
                            {growth > 0 && <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 font-mono font-bold">+{growth.toFixed(0)}%</span>}
                            {r.at && <span className="text-[9px] font-mono text-white/50 bg-white/5 px-1.5 py-0.5 rounded">{fmtTimeAgo(r.at)}</span>}
                          </div>
                          <span className="text-white/60 text-[11px] truncate max-w-[150px]">{r.name || `Token ${r.mint.slice(0,4)}`}</span>
                          <button
                            onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(r.mint); }}
                            className="font-mono text-[9px] text-white/40 hover:text-white/80 transition-colors flex items-center gap-1 mt-0.5 w-fit"
                            title="Click to copy CA"
                          >
                            {r.mint.slice(0, 6)}...{r.mint.slice(-6)}
                            <Copy className="size-2.5 opacity-60" />
                          </button>
                          <div className="flex items-center gap-2 text-[10px] mt-1 font-mono flex-wrap">
                            <span className="text-white/50">Whale: <span className="text-emerald-400 font-bold">{r.top1 != null ? `${r.top1.toFixed(1)}%` : '—'}</span></span>
                            <span className="text-white/50">Dev: <span className="text-white/70">{r.creatorPct != null ? `${r.creatorPct.toFixed(1)}%` : '0%'}</span></span>
                            {r.baselineMcUsd && <span className="text-white/50">Base: <span className="text-white/70">${Math.round(r.baselineMcUsd).toLocaleString()}</span></span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col items-end shrink-0 pl-2">
                        <span className="text-emerald-400 font-bold font-mono text-base">${Math.round(r.mcUsd || 0).toLocaleString()}</span>
                        <span className="text-white/40 text-[9px] font-mono">MCAP</span>
                      </div>
                    </div>
                  );
                })}
              {watchdog.revivals.length === 0 && (
                <p className="text-center text-white/30 text-xs py-6">No revivals tracked yet</p>
              )}
            </div>
          )}

                    {/* AI Confidence sort — from recommendations */}
          {alphaSort === "confidence" && (
            <div className="space-y-4">
              {recommendations
                .filter(r => !r.isDead && !r.isRugged)
                .sort((a, b) => b.confidence - a.confidence)
                .map(r => (
                  <AlphaTokenRow
                    key={r.mint}
                    target={{
                      mint: r.mint,
                      coin: r.coin,
                      name: r.name,
                      imageUri: r.imageUri,
                      badge: "🤖",
                      confidence: r.confidence,
                      whaleSol: r.whaleSol > 0 ? r.whaleSol : undefined,
                      desc: r.desc
                    }}
                    isSelected={caMintInput === r.mint}
                    onOpen={setCaMintInput}
                  />
                ))}
              {recommendations.filter(r => !r.isDead && !r.isRugged).length === 0 && (
                <p className="text-center text-white/30 text-xs py-6">No AI signals yet</p>
              )}
            </div>
          )}

                    {/* Whale SOL sort — from recommendations */}
          {alphaSort === "whale" && (
            <div className="space-y-4">
              {recommendations
                .filter(r => !r.isDead && !r.isRugged && r.whaleSol > 0)
                .sort((a, b) => b.whaleSol - a.whaleSol)
                .map(r => (
                  <AlphaTokenRow
                    key={r.mint}
                    target={{
                      mint: r.mint,
                      coin: r.coin,
                      name: r.name,
                      imageUri: r.imageUri,
                      badge: "🐋",
                      confidence: r.confidence,
                      whaleSol: r.whaleSol,
                      desc: r.desc
                    }}
                    isSelected={caMintInput === r.mint}
                    onOpen={setCaMintInput}
                  />
                ))}
              {recommendations.filter(r => !r.isDead && !r.isRugged && r.whaleSol > 0).length === 0 && (
                <p className="text-center text-white/30 text-xs py-6">No whale activity detected</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* FAQ Section */}
      {viewMode !== "alpha" && (
        <div className="bg-[#1a1b1e] border-t border-white/5 p-5 pb-10">
          <h3 className="text-white font-bold mb-4 flex items-center gap-2"><ShieldCheck className="size-5 text-purple-400" /> Security & FAQ</h3>
          <div className="space-y-2">
            {[
              { id: "storage", title: "How your funds are stored", content: "Your funds exist entirely on the Solana blockchain. This wallet simply holds the keys to access them locally in your browser's secure storage." },
              { id: "security", title: "How your wallets are secured", content: "Your private keys are encrypted and never leave this device. We do not store or transmit them to any backend server." },
              { id: "scams", title: "Tips for avoiding scams", content: "Never share your private key or seed phrase with anyone. Verify all transactions before approving them, and only connect to trusted dApps." }
            ].map(faq => (
              <div key={faq.id} className="border border-white/10 rounded-xl overflow-hidden bg-black/20">
                <button 
                  onClick={() => setFaqOpen(faqOpen === faq.id ? null : faq.id)}
                  className="w-full p-4 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
                >
                  <span className="text-sm font-bold text-white/80">{faq.title}</span>
                  {faqOpen === faq.id ? <ChevronUp className="size-4 text-white/40" /> : <ChevronDown className="size-4 text-white/40" />}
                </button>
                {faqOpen === faq.id && (
                  <div className="p-4 text-sm text-white/50 border-t border-white/5 bg-black/10">
                    {faq.content}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sheets Overlay */}
      {activeSheet !== "none" && (
        <div 
          className="absolute inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity"
          onClick={() => setActiveSheet("none")}
        />
      )}



      {/* Accounts Sheet */}
      <div className={`absolute bottom-0 left-0 right-0 bg-[#1a1b1e] border-t border-white/10 rounded-t-[2rem] p-6 min-h-[70vh] flex flex-col z-50 transition-transform duration-300 ${activeSheet === "accounts" ? "translate-y-0 shadow-[0_-20px_40px_rgba(0,0,0,0.5)]" : "translate-y-full"}`}>
        <div className="w-12 h-1.5 bg-white/20 rounded-full mx-auto mb-6 cursor-pointer" onClick={() => setActiveSheet("none")} />
        <h3 className="text-2xl font-black text-white mb-6 text-center">Accounts</h3>
        
        <div className="space-y-3 flex-1 overflow-y-auto">
          {accounts.map(acc => (
            <div 
              key={acc.id} 
              onClick={() => { switchAccount(acc.id); setActiveSheet("none"); }}
              className={`p-4 rounded-2xl border transition-all cursor-pointer flex items-center justify-between ${activeAccountId === acc.id ? "bg-purple-500/10 border-purple-500/50" : "bg-white/5 border-white/10 hover:bg-white/10"}`}
            >
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-full bg-gradient-to-tr from-purple-500 to-indigo-500 flex items-center justify-center overflow-hidden shrink-0 relative group"
                  onClick={(e) => {
                    e.stopPropagation();
                    const input = document.createElement("input");
                    input.type = "file";
                    input.accept = "image/*";
                    input.onchange = (ev) => {
                      const file = (ev.target as HTMLInputElement).files?.[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = (e) => {
                          const base64 = e.target?.result as string;
                          updateAccount(acc.id, { profilePicture: base64 });
                        };
                        reader.readAsDataURL(file);
                      }
                    };
                    input.click();
                  }}
                  title="Click to change avatar"
                >
                  {acc.profilePicture ? (
                    <img src={acc.profilePicture} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-white font-bold">{acc.name.charAt(0)}</span>
                  )}
                  <div className="absolute inset-0 bg-black/50 hidden group-hover:flex items-center justify-center transition-colors">
                    <span className="text-[10px] font-bold text-white">Edit</span>
                  </div>
                </div>
                <div className="flex flex-col">
                  <span className="text-white font-bold">{acc.name}</span>
                  <span className="text-white/40 text-xs font-mono">{acc.publicKey.slice(0,4)}...{acc.publicKey.slice(-4)}</span>
                </div>
              </div>
              {activeAccountId === acc.id && <Check className="size-5 text-purple-400" />}
            </div>
          ))}
        </div>
        
        <div className="flex gap-3 mt-6">
          <button 
            onClick={handleCreateAccount}
            className="flex-1 flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 text-white py-4 rounded-2xl font-bold transition-all active:scale-95 border border-white/5"
          >
            <Plus className="size-5" />
            {t("wallet.generateAccount")}
          </button>
          <button 
            onClick={() => setActiveSheet("import")}
            className="flex-1 flex items-center justify-center gap-2 bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 py-4 rounded-2xl font-bold transition-all active:scale-95 border border-purple-500/30"
          >
            <ArrowDownLeft className="size-5" />
            Import Key
          </button>
        </div>
      </div>

      {/* Settings Sheet */}
      <div className={`absolute bottom-0 left-0 right-0 bg-[#1a1b1e] border-t border-white/10 rounded-t-[2rem] p-6 min-h-[50vh] flex flex-col z-50 transition-transform duration-300 ${activeSheet === "settings" ? "translate-y-0 shadow-[0_-20px_40px_rgba(0,0,0,0.5)]" : "translate-y-full"}`}>
        <div className="w-12 h-1.5 bg-white/20 rounded-full mx-auto mb-6" onClick={() => setActiveSheet("none")} />
        <h3 className="text-xl font-bold text-white mb-6">Wallet Settings</h3>
        
        <div className="space-y-4">
          {/* Launch Provider Filter */}
          <div className="bg-white/5 border border-white/10 p-4 rounded-2xl">
            <h4 className="text-white font-medium mb-3">Launch Provider Filter</h4>
            <div className="flex flex-wrap gap-2">
              {[
                { id: "Pump.fun", name: "Pump.fun", icon: "💊" },
                { id: "Moonshot", name: "Moonshot", icon: "🌙" },
                { id: "Raydium", name: "Raydium", icon: "⚡️" },
                { id: "Meteora", name: "Meteora", icon: "☄️" },
              ].map((provider) => {
                const isSelected = launchProviderFilter.includes(provider.id);
                return (
                  <button
                    key={provider.id}
                    onClick={() => {
                      if (isSelected) {
                        setLaunchProviderFilter(launchProviderFilter.filter((p: string) => p !== provider.id));
                      } else {
                        setLaunchProviderFilter([...launchProviderFilter, provider.id]);
                      }
                    }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all border ${
                      isSelected
                        ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30 shadow-[0_0_10px_rgba(16,185,129,0.1)] hover:bg-emerald-500/20"
                        : "bg-white/5 text-white/40 border-white/10 hover:bg-white/10 hover:text-white/70"
                    }`}
                  >
                    <span className="text-sm leading-none drop-shadow-sm">{provider.icon}</span>
                    {provider.name}
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] text-white/40 mt-2">Filter coins by their on-chain launch provider.</p>
          </div>

          <div className="bg-white/5 border border-white/10 p-4 rounded-2xl">
            <div className="flex justify-between items-center mb-2">
              <span className="text-white font-medium">{t("wallet.advancedView")}</span>
              <button 
                type="button"
                onClick={() => setIsAdvanced(!isAdvanced)} 
                className={`text-[10px] font-bold px-2 py-0.5 rounded border transition-all ${isAdvanced ? "bg-cyan-500/15 text-cyan-400 border-cyan-500/30" : "bg-white/5 text-white/40 border-white/10"}`}
              >
                {isAdvanced ? "Enabled" : "Disabled"}
              </button>
            </div>
            
            {isAdvanced && (
              <div className="space-y-4 mt-4 pt-4 border-t border-white/10">
                {/* Private Key Display */}
                <div>
                  <div className="flex justify-between items-center mb-1.5">
                    <span className="text-xs text-white/50">{t("wallet.secretKey")}</span>
                    <button onClick={() => setShowSecret(!showSecret)} className="text-white/40 hover:text-white">
                      {showSecret ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
                    </button>
                  </div>
                  {showSecret ? (
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[10px] text-rose-400 break-all bg-rose-500/10 p-2 rounded-lg border border-rose-500/20 flex-1">
                        {secretKey}
                      </span>
                      <button onClick={copySecret} className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors text-white">
                        <Copy className="size-3.5" />
                      </button>
                    </div>
                  ) : (
                    <p className="text-white/40 text-xs">••••••••••••••••••••••••••••••••••••••••••••</p>
                  )}
                </div>

                {/* Offline Sync Toggle */}
                <div className="bg-black/20 p-3 rounded-xl border border-white/[0.04]">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs text-white/70">{t("wallet.syncStatus")}</span>
                    <span className={`inline-block text-[9px] font-bold px-2 py-0.5 rounded-full ${isSynced ? "bg-emerald-500/25 text-emerald-400" : "bg-red-500/25 text-red-400"}`}>
                      {isSynced ? t("wallet.offlineActive") : t("wallet.offlineInactive")}
                    </span>
                  </div>
                  <p className="text-[9px] text-white/30 leading-snug mb-3">
                    Sync your key to enable server-side monitored limit orders and offline copy trading. Credentials are encrypted securely.
                  </p>
                  {isSynced ? (
                    <button
                      onClick={handleUnsyncWallet}
                      disabled={isSyncing}
                      className="w-full text-center py-1.5 rounded-lg bg-red-600/20 hover:bg-red-600/30 text-red-400 text-[11px] font-bold border border-red-500/20 transition-all"
                    >
                      {isSyncing ? "Unsyncing..." : t("wallet.unsyncButton")}
                    </button>
                  ) : (
                    <button
                      onClick={handleSyncWallet}
                      disabled={isSyncing}
                      className="w-full text-center py-1.5 rounded-lg bg-cyan-600/20 hover:bg-cyan-600/30 text-cyan-400 text-[11px] font-bold border border-cyan-500/20 transition-all"
                    >
                      {isSyncing ? "Syncing..." : t("wallet.syncButton")}
                    </button>
                  )}
                </div>

                {/* Copy Trading Target Configurator */}
                {isSynced && (
                  <div className="border-t border-white/5 pt-4 mt-3 space-y-3">
                    <h5 className="text-xs font-bold text-purple-300">{t("wallet.copyTradeSetup")}</h5>
                    
                    <form onSubmit={handleAddWhale} className="space-y-2">
                      <div className="flex justify-between items-center pb-1">
                        <span className="text-[10px] text-white/40">Target</span>
                        <button
                          type="button"
                          onClick={() => {
                            setTargetWallet("FLYBRAIN_OS");
                            setTargetName("FlyBrain Neural OS");
                          }}
                          className="text-[9px] font-bold text-green-400 bg-green-500/10 hover:bg-green-500/20 px-2 py-0.5 rounded border border-green-500/20 transition-all flex items-center gap-1"
                        >
                          🧠 Auto-Copy FlyBrain
                        </button>
                      </div>
                      <input
                        type="text"
                        placeholder={t("wallet.targetAddress")}
                        value={targetWallet}
                        onChange={(e) => setTargetWallet(e.target.value)}
                        className="w-full text-[11px] bg-black/40 border border-white/10 rounded-lg px-2.5 py-1.5 text-white placeholder-white/20 font-mono"
                        required
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="text"
                          placeholder="Whale Label (e.g. Whale 1)"
                          value={targetName}
                          onChange={(e) => setTargetName(e.target.value)}
                          className="w-full text-[11px] bg-black/40 border border-white/10 rounded-lg px-2.5 py-1.5 text-white placeholder-white/20"
                        />
                        <input
                          type="number"
                          step="any"
                          placeholder={t("wallet.copyAmount")}
                          value={buyAmount}
                          onChange={(e) => setBuyAmount(e.target.value)}
                          className="w-full text-[11px] bg-black/40 border border-white/10 rounded-lg px-2.5 py-1.5 text-white font-mono"
                        />
                      </div>
                      <button
                        type="submit"
                        className="w-full text-center py-1.5 bg-purple-600 hover:bg-purple-500 rounded-lg text-[11px] font-bold tracking-wide transition-all"
                      >
                        {t("wallet.addWhale")}
                      </button>
                    </form>

                    {/* Copy Targets List */}
                    <div className="space-y-1.5 max-h-40 overflow-y-auto pt-2">
                      <div className="text-[10px] font-bold text-white/30 uppercase tracking-wide">
                        {t("wallet.activeWhales")} ({copyTargets.length})
                      </div>
                      {copyTargets.length === 0 ? (
                        <p className="text-[9px] text-white/20 italic">No copy trade targets. Add a target address above.</p>
                      ) : (
                        copyTargets.map((target) => (
                          <div key={target.id} className="flex justify-between items-center p-2 rounded-lg bg-black/30 border border-white/[0.04] text-[10px]">
                            <div className="min-w-0 flex-1 pr-1.5">
                              <div className="font-semibold text-white/80">{target.name}</div>
                              <div className="text-white/40 font-mono text-[9px] truncate">{target.targetWallet}</div>
                              <div className="text-purple-300 font-mono text-[9px] mt-0.5">Amount: {target.buyAmountSol} SOL</div>
                            </div>
                            <button
                              onClick={() => handleDeleteWhale(target.id)}
                              className="text-red-400/50 hover:text-red-400 p-1 hover:bg-red-500/10 rounded transition-all shrink-0"
                            >
                              Delete
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Import Account Sheet */}
      <div className={`absolute bottom-0 left-0 right-0 bg-[#1a1b1e] border-t border-white/10 rounded-t-[2rem] p-6 min-h-[50vh] flex flex-col z-50 transition-transform duration-300 ${activeSheet === "import" ? "translate-y-0 shadow-[0_-20px_40px_rgba(0,0,0,0.5)]" : "translate-y-full"}`}>
        <div className="w-12 h-1.5 bg-white/20 rounded-full mx-auto mb-6 cursor-pointer" onClick={() => setActiveSheet("none")} />
        <h3 className="text-2xl font-black text-white mb-2 text-center">Import Wallet</h3>
        <p className="text-white/50 text-sm text-center mb-6">Enter a Base58 Private Key to import an existing Solana wallet.</p>
        
        {importError && <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-xl mb-4 text-red-400 text-sm">{importError}</div>}
        
        <div className="mb-4">
          <label className="text-xs font-bold uppercase tracking-wider text-white/50 mb-2 block">Private Key (Base58)</label>
          <input 
            type="password" 
            value={importKey}
            onChange={(e) => setImportKey(e.target.value)}
            placeholder="Private Key..."
            className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition-colors font-mono text-sm"
          />
        </div>

        <button 
          onClick={() => {
            setImportError("");
            if (!importKey.trim()) { setImportError("Please enter a private key."); return; }
            const success = importAccount(importKey.trim());
            if (success) {
              setImportKey("");
              setActiveSheet("none");
            } else {
              setImportError("Invalid private key. Please check and try again.");
            }
          }}
          className="w-full bg-gradient-to-r from-purple-500 to-indigo-500 text-white font-black py-4 rounded-xl mt-auto shadow-lg shadow-purple-500/25 active:scale-95 transition-transform"
        >
          Import & Connect
        </button>
      </div>

    </div>
  );
}
